import { apiClient } from '@/core/infra_api_client'

// ============= Types =============

export type BackupStatus = 'scheduled' | 'running' | 'completed' | 'failed'

export interface BackupSchedule {
  id: string
  environmentId: string
  name: string
  cronExpression: string
  retentionDays: number
  enabled: boolean
  lastRunAt?: string
  nextRunAt?: string
  createdAt: string
  updatedAt: string
}

export interface BackupSnapshot {
  id: string
  scheduleId: string
  environmentId: string
  status: BackupStatus
  sizeBytes?: number
  startedAt: string
  completedAt?: string
  expiresAt: string
}

export interface TriggerBackupRequest {
  environmentId: string
  scheduleId?: string
}

export interface RestoreBackupRequest {
  targetEnvironmentId?: string
}

export interface PaginatedResponse<T> {
  data: T[]
  total: number
  page: number
  perPage: number
  totalPages: number
}

// ============= API =============

const BASE_PATH = 'backups'

export async function listSchedules(params?: {
  page?: number
  perPage?: number
  environmentId?: string
}): Promise<PaginatedResponse<BackupSchedule>> {
  const searchParams: Record<string, string | number | boolean> = {}
  if (params?.page) searchParams['page'] = params.page
  if (params?.perPage) searchParams['per_page'] = params.perPage
  if (params?.environmentId) searchParams['environment_id'] = params.environmentId

  return apiClient.get<PaginatedResponse<BackupSchedule>>(`${BASE_PATH}/schedules`, searchParams)
}

export async function listSnapshots(params?: {
  page?: number
  perPage?: number
  environmentId?: string
  status?: string
}): Promise<PaginatedResponse<BackupSnapshot>> {
  const searchParams: Record<string, string | number | boolean> = {}
  if (params?.page) searchParams['page'] = params.page
  if (params?.perPage) searchParams['per_page'] = params.perPage
  if (params?.environmentId) searchParams['environment_id'] = params.environmentId
  if (params?.status) searchParams['status'] = params.status

  return apiClient.get<PaginatedResponse<BackupSnapshot>>(`${BASE_PATH}/snapshots`, searchParams)
}

export async function triggerBackup(data: TriggerBackupRequest): Promise<BackupSnapshot> {
  return apiClient.post<BackupSnapshot, TriggerBackupRequest>(`${BASE_PATH}/trigger`, data)
}

export async function restoreBackup(
  snapshotId: string,
  data?: RestoreBackupRequest
): Promise<BackupSnapshot> {
  return apiClient.post<BackupSnapshot, RestoreBackupRequest>(
    `${BASE_PATH}/snapshots/${snapshotId}/restore`,
    data
  )
}
